import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslate } from '@tolgee/react';
import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";
import "../../Assets/styles/customPhoneSelect.css";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { useDefaultCountry } from "../../hooks/useGeolocationDb";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import { useAuthService } from "../../Context/AuthContext";
import { useNotify } from "../../Helpers/Notifications";
import { RecaptchaVerifier, signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import OtpInput from "react-otp-input";
import LocalStorage from "../../Helpers/LocalStorage";

const LinkPhoneNumberModal = ({ closeLinkPhoneNumberModal }) => {
  const { t } = useTranslate();
  const [phase, setPhase] = useState(1);
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("RO");
  const [wrongCode, setWrongCode] = useState(false);
  const [otp, setOtp] = useState("");
  const [defaultCountry] = useDefaultCountry();
  const { notifyError, notifySuccess } = useNotify();
  const {
    canLink,
    signInWithPhone,
    verifyOtpCodeAndSignIn,
    linkPhoneNumber,
    handleLoginUser,
    disableAuthObserver,
  } = useAuthService();
  const { setLoading } = useGenericComponentsStateService();
  const confirmationRes = useRef();
  const ssoAuth = LocalStorage.getSSOFlag();
  const initializeReCaptcha = () => {
    if (window.recaptchaVerifier == null) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: (response) => {
            // reCAPTCHA solved, allow signInWithPhoneNumber.
            //onSignInSubmit();
          },
        }
      );
    }
  };

  const reinitializeRecaptchaContainer = () => {
    window.recaptchaVerifier = null;
    let element = document.getElementById("recaptcha-container");
    let parent = element.parentElement;
    parent.removeChild(element);
    let newElement = document.createElement("div");
    newElement.setAttribute("id", "recaptcha-container");
    parent.appendChild(newElement);
  };

  const resetRechaptcha = () => {
    window.recaptchaVerifier.recaptcha.reset();
  };

  const handleTryLinkPhoneNumber = (phone) => {
    const sendData = {};
    sendData.phone_number = phone;
    canLink(
      sendData,
      (response) => {
        let canLink = response.data.can_link;
        if (canLink) {
          onSignInSubmit();
        } else {
          notifyError(t('settings.link_phone_error'));
        }
      },
      (error) => {
        console.log(error);
        notifyError(t('general.something_wrong'));
      }
    );
  };

  const onSignInSubmit = (e) => {
    if (window.recaptchaVerifier == null) {
      initializeReCaptcha();
    } else {
      resetRechaptcha();
    }
    setLoading(true);
    signInWithPhone(
      phone,
      window.recaptchaVerifier,
      function (confirmationResult) {
        setLoading(false);
        confirmationRes.current = confirmationResult;
        setPhase(2);
        notifySuccess(`${t('login.code_sent')}`);
      },
      function (error) {
        notifyError(`${t('general.something_wrong')}`);
        console.log(error, "err");
        setLoading(false);
      }
    );
  };

  const handleVerifyCode = (otp) => {
    setLoading(true);
    disableAuthObserver.current = true;
    verifyOtpCodeAndSignIn(
      confirmationRes.current,
      otp,
      function (response) {
        reinitializeRecaptchaContainer();
        setWrongCode(false);
        let token = response.user.accessToken;
        let refresh = response.user.stsTokenManager.refreshToken;
        handleLinkPhoneNumber(token, refresh);
      },
      function (error) {
        setOtp("");
        setWrongCode(true);
        console.log(error);
        notifyError(`${t('general.something_wrong')}`);
        setLoading(false);
        disableAuthObserver.current = false;
      }
    );
  };

  const handleLinkPhoneNumber = (token, refresh) => {
    linkPhoneNumber(
      token,
      (response) => {
        const user = response.data.user;
        if (ssoAuth === "true") {
          signOutFirebase();
        } else {
          LocalStorage.setAuthenthicationToken(token);
          LocalStorage.setRefreshToken(refresh);
        }

        handleLoginUser(user);

        setLoading(false);
        disableAuthObserver.current = false;
        closeLinkPhoneNumberModal();
      },
      (error) => {
        setLoading(false);
        notifyError(t('general.something_wrong'));
        disableAuthObserver.current = false;
      }
    );
  };

  const signOutFirebase = () => {
    signOut(auth)
      .then(() => {
        closeLinkPhoneNumberModal();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const phase1 = (
    <ContentContainer>
      <InfoText>
        {t('settings.link_phone_number_info_text')}
      </InfoText>
      <CustomPhoneWrapper>
        <PhoneInput
          international
          defaultCountry={defaultCountry}
          name="phoneNumber"
          placeholder={t('profile.phone_number')}
          value={phone}
          onChange={(e) => setPhone(e)}
          onCountryChange={(e) => setCountryCode(e)}
          limitMaxLength
        />
      </CustomPhoneWrapper>
      <PrimaryButton
        disabled={phone?.length < 8}
        style={{ padding: "16px" }}
        text={t('settings.send_verification_and_link_phone_number')}
        onClick={() => handleTryLinkPhoneNumber(phone)}
      />
    </ContentContainer>
  );

  const phase2 = (
    <ContentContainer>
      <InfoText>
        {t('settings.link_phone_number_info_text_code')}
      </InfoText>
      <OtpInputWrapper>
        <OtpInput
          containerStyle={{
            display: "flex",
            justifyContent: "space-around",
            width: "100%",
            gap: "5px",
          }}
          shouldAutoFocus
          inputStyle={wrongCode ? { borderColor: "red" } : {}}
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span> </span>}
          renderInput={(props) => <input id="opt-input" {...props} />}
        />
      </OtpInputWrapper>
      {wrongCode && (
        <WrongCodeStatus>
          <p>{t('login.wrong_validation_code')}</p>
        </WrongCodeStatus>
      )}
      <PrimaryButton
        disabled={otp.length !== 6}
        style={{ padding: "16px" }}
        text={t('settings.validate_and_link_phone_number')}
        onClick={() => handleVerifyCode(otp)}
      />
    </ContentContainer>
  );

  var content = phase1;

  switch (phase) {
    case 1:
      content = phase1;
      break;
    case 2:
      content = phase2;
      break;

    default:
      break;
  }

  useEffect(() => {
    reinitializeRecaptchaContainer();

    return () => {
      reinitializeRecaptchaContainer();
    }
  }, []);

  return (
    <FullScreenContainer>
      <Container>
        <Header>
          <Title>{t('settings.link_phone_number')}</Title>
          <CloseBtn>
            <CloseIcon onClick={closeLinkPhoneNumberModal} />
          </CloseBtn>
        </Header>
        {content}
      </Container>
    </FullScreenContainer>
  );
};

export default LinkPhoneNumberModal;

const FullScreenContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  left: 0;
  top: 0;
  z-index: 2;
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 25px;
  width: 100%;
  max-width: 500px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  gap: 20px;
  border-radius: 10px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;
const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.menu.selectedOpionBackground};
  stroke: ${({ theme }) => theme.sidebar.borderColor};
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: 0.04px;
  vertical-align: middle;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const CustomPhoneWrapper = styled.div`
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
  padding: 0 0 0 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  width: 100%;

  input {
    padding: 14px 16px;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
    border-radius: 10px;
    border: none;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    &:focus {
      outline: none;
    }
  }

  .PhoneInputCountry {
    border: none;
    background: ${({ theme }) => theme.chat.selectedBackground};
    border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    align-self: center;
    margin-right: 0;
  }
  .PhoneInput {
    width: 100%;
  }
  .PhoneInputCountrySelectArrow {
    display: none;
  }
  .PhoneInputCountryIcon--border {
    box-shadow: none;
  }
  .PhoneInputCountrySelect:focus + .PhoneInputCountryIcon--border {
    box-shadow: none;
  }
`;

const InfoText = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  vertical-align: middle;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  span {
    color: ${({ theme }) => theme.menu.selectedTextColor};
    cursor: pointer;
  }
`;

const OtpInputWrapper = styled.div`
  display: flex;
  width: 100%;

  input {
    padding: 14px 16px;
    min-width: 50px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
    color: ${({ theme }) => theme.menu.primaryTextColor};
    background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
    width: 100%;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    &:focus {
      outline: none;
    }
  }
`;
const WrongCodeStatus = styled.div`
  p {
    color: ${({ theme }) => theme.menu.deleteColor};
    font-size: 14px;
    font-weight: 500;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
`;
